import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'

import Box from '@components/kit/box'
import Card from '@components/kit/card'
import { Column, Row } from '@components/kit/grid'
import Loading from '@components/kit/loading'
import Text from '@components/kit/text'

import { ChatContext } from '../chat.web'
import { useHealthTrackingStore } from '@store/HealthTracking'
import { IHealthTrackingA1cLabs } from '@store/HealthTracking/types'
import Pressable from '@components/kit/pressable'
import { ChevronDownIcon } from '@components/kit/icons'

const PAGE_SIZE = 10
const RETRACTED_PAGE_SIZE = 5

function A1cLabs() {
  const { userId } = useContext(ChatContext)
  const { getA1cLabsLog } = useHealthTrackingStore.getState()
  const [a1cLabsLog, setA1cLabsLog] = useState<IHealthTrackingA1cLabs | null>(
    null
  )
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [currentPageSize, setCurrentPageSize] =
    useState<number>(RETRACTED_PAGE_SIZE)
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const isRetractable = a1cLabsLog?.results.length > RETRACTED_PAGE_SIZE

  const toggleExpanded = () => {
    if (isExpanded) {
      setCurrentPageSize(RETRACTED_PAGE_SIZE)
    } else {
      setCurrentPageSize(PAGE_SIZE)
    }
    setIsExpanded(!isExpanded)
  }

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      const data = await getA1cLabsLog(userId, currentPage, PAGE_SIZE)
      setA1cLabsLog(data)
      setIsLoading(false)
    }
    fetchData()
  }, [userId, currentPage])

  return (
    <React.Fragment>
      <Row
        mt='24'
        flexDirection='row'
        alignItems='center'
        justifyContent='space-between'
      >
        <Text ml='12' mb='08' font='display.16.bold'>
          A1C Labs
        </Text>
        {isRetractable && (
          <Pressable
            mr='12'
            onPress={toggleExpanded}
            width='20'
            height='20'
            justifyContent='center'
            alignItems='center'
            style={{ transform: [{ rotate: !isExpanded ? '0deg' : '180deg' }] }}
          >
            <ChevronDownIcon fill={'shade07'} width={20} height={20} />
          </Pressable>
        )}
      </Row>
      <Card mt='08' px='00' py='00'>
        {isLoading ? (
          <Box pt='24'>
            <Loading mt='20' py='20' />
          </Box>
        ) : (
          <React.Fragment>
            <Row m='16'>
              <Column width='50%'>
                <Text font='text.12.bold'>Date</Text>
              </Column>
              <Column width='50%'>
                <Text font='text.12.bold'>A1C (%)</Text>
              </Column>
            </Row>

            {a1cLabsLog?.results.length === 0 && (
              <Row mx='16' my='12'>
                <Text font='text.14.medium'>No data available yet.</Text>
              </Row>
            )}

            {a1cLabsLog?.results.map((a1cLog, index) => {
              if (index >= currentPageSize) {
                return null
              }
              const isRecent =
                moment().diff(moment(a1cLog.timestamp), 'days') <= 7
              return (
                <Row key={index} mx='16' my='12'>
                  <Column width='50%' flexDirection='row' alignItems='center'>
                    <Text font='text.14.medium'>
                      {moment(a1cLog.timestamp, 'YYYY-MM-DDTHH:mm:ssZ').format(
                        'MM/DD/YYYY'
                      )}
                    </Text>
                  </Column>
                  <Column width='50%' flexDirection='row' alignItems='center'>
                    {isRecent && (
                      <Box
                        backgroundColor='paleRed'
                        width='12px'
                        height='12px'
                        mr='08'
                        borderRadius='xlarge'
                      />
                    )}
                    <Text font='text.14.medium'>{a1cLog.value}</Text>
                  </Column>
                </Row>
              )
            })}
          </React.Fragment>
        )}
      </Card>
    </React.Fragment>
  )
}

export default A1cLabs
